import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Constante } from '../../constante';
import { ProductoDetalleApi, ProductoListApi, ProductoListQuery, ProductoRegisterPayload } from '../models/api/producto-api';
import { PagedResultApi, ResponseApi } from '../models/response-api';
import { AppUtil } from '../utility/app-util';

@Injectable({ providedIn: 'root' })
export class ProductoService {
    private http = inject(HttpClient);
    private url = AppUtil.addTrailingSlash(environment.URL_API_PEDIDOS) + 'api/producto/';

    listProductos(query: ProductoListQuery = {}): Observable<PagedResultApi<ProductoListApi>> {
        let params = new HttpParams()
            .set('pageNumber', String(query.pageNumber ?? 1))
            .set('pageSize', String(query.pageSize ?? Constante.PAGINATION_SIZE));

        if (query.categoriaUuid) {
            params = params.set('categoriaUuid', query.categoriaUuid);
        }
        if (query.precioDesde != null) {
            params = params.set('precioDesde', String(query.precioDesde));
        }
        if (query.precioHasta != null) {
            params = params.set('precioHasta', String(query.precioHasta));
        }
        if (query.orden) {
            params = params.set('orden', query.orden);
        }
        if (query.key) {
            params = params.set('key', query.key);
        }
        if (query.incluirOcultos) {
            params = params.set('incluirOcultos', 'true');
        }

        return this.http.get<ResponseApi<PagedResultApi<ProductoListApi>>>(this.url + 'list-productos', { params }).pipe(
            map((res) => {
                if (res.success === Constante.REQUEST_STATUS_OK && res.data) {
                    return res.data;
                }
                return { total: 0, page: 1, pageSize: query.pageSize ?? Constante.PAGINATION_SIZE, data: [] };
            })
        );
    }

    listMasVendidos(): Observable<ProductoListApi[]> {
        return this.http.get<ResponseApi<ProductoListApi[]>>(this.url + 'list-mas-vendidos').pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && Array.isArray(res.data) ? res.data : []))
        );
    }

    getProductoBySlug(slug: string): Observable<ProductoDetalleApi | null> {
        return this.http.get<ResponseApi<ProductoDetalleApi>>(this.url + 'get-producto/' + encodeURIComponent(slug)).pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK && res.data ? res.data : null))
        );
    }

    createProducto(body: ProductoRegisterPayload): Observable<string | null> {
        return this.http.post<ResponseApi<{ id: string }>>(this.url + 'create-producto', body).pipe(
            map((res) => (res.success === Constante.REQUEST_STATUS_OK ? res.data?.id ?? null : null))
        );
    }

    updateProducto(uuid: string, body: ProductoRegisterPayload): Observable<boolean> {
        return this.http.put<ResponseApi<unknown>>(this.url + 'update-producto/' + uuid, body).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }

    deleteProducto(uuid: string): Observable<boolean> {
        return this.http.delete<ResponseApi<unknown>>(this.url + 'delete-producto/' + uuid).pipe(
            map((res) => res.success === Constante.REQUEST_STATUS_OK)
        );
    }
}
